'use client';

/**
 * Rx control center — platform scope (`/rx`).
 *
 * The TGD Care desk: every affiliate mailbox handled by Tagada, all patient
 * conversations, replies sent as the brand. Superadmins can also move a
 * mailbox to a partner desk (identity stays the brand's).
 */

import * as React from 'react';
import toast from 'react-hot-toast';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { trpc } from '@/lib/api/trpc';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { RxSupportDesk, type DeskMailbox } from '@/components/rx-support/RxSupportDesk';
import { mergePages, useRxSupportDeskState } from '@/components/rx-support/useRxSupportDeskState';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export default function AdminRxSupportPage() {
  const state = useRxSupportDeskState();
  const utils = trpc.useUtils();
  const api = trpc.rxSupport.admin;
  // Moving a mailbox between desks is rx_admin-only; rx_ops just works the queue.
  const { isSuperAdmin, isRxAdmin } = useSessionClaims();
  const canMoveDesk = isSuperAdmin || isRxAdmin;
  // Partner desks come from the DB roster (the code registry is server / edge
  // only). Gated server-side; without access the list is empty and the select
  // still shows the mailbox's current partner.
  const partnerSlugs = trpc.partners.listActivePartnerSlugs.useQuery(undefined, {
    enabled: canMoveDesk,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const partnerDeskOptions = React.useMemo(
    () => (partnerSlugs.data ?? []).map((p) => p.slug),
    [partnerSlugs.data],
  );

  const counts = api.counts.useQuery({}, { refetchInterval: 30_000 });
  const mailboxes = api.mailboxes.useQuery({}, { refetchInterval: 60_000 });
  const list = api.listConversations.useQuery(state.listInput, { refetchInterval: 20_000 });
  const detail = api.getConversation.useQuery(
    { conversationId: state.selectedId ?? '' },
    { enabled: Boolean(state.selectedId), refetchInterval: 20_000 },
  );

  const context = api.context.useQuery(
    { conversationId: state.selectedId ?? '' },
    { enabled: Boolean(state.selectedId), refetchInterval: 60_000 },
  );

  const invalidate = React.useCallback(async () => {
    await Promise.all([
      utils.rxSupport.admin.context.invalidate(),
      utils.rxSupport.admin.listConversations.invalidate(),
      utils.rxSupport.admin.counts.invalidate(),
      utils.rxSupport.admin.mailboxes.invalidate(),
      state.selectedId
        ? utils.rxSupport.admin.getConversation.invalidate({ conversationId: state.selectedId })
        : Promise.resolve(),
    ]);
  }, [utils, state.selectedId]);

  const reply = api.reply.useMutation({ onSuccess: invalidate, onError: (e) => toast.error(errMsg(e)) });
  const note = api.addNote.useMutation({ onSuccess: invalidate, onError: (e) => toast.error(errMsg(e)) });
  const setStatus = api.setStatus.useMutation({
    onSuccess: invalidate,
    onError: (e) => toast.error(errMsg(e)),
  });
  const assign = api.assign.useMutation({ onSuccess: invalidate, onError: (e) => toast.error(errMsg(e)) });
  const refreshMailbox = api.refreshMailbox.useMutation({
    onSuccess: (m) => {
      toast.success(`${m.address}: send ${m.domainStatus}, receive ${m.receivingStatus}`);
      void invalidate();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const setHandler = api.setHandler.useMutation({
    onSuccess: (m) => {
      toast.success(
        `${m.address} now handled by ${m.handledBy === 'platform' ? 'TGD Care' : m.handlerPartner}`,
      );
      void invalidate();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const refundCase = api.refundCase.useMutation({ onSuccess: invalidate, onError: (e) => toast.error(errMsg(e)) });
  const cancelRenewal = api.cancelRenewal.useMutation({
    onSuccess: invalidate,
    onError: (e) => toast.error(errMsg(e)),
  });
  const resendTracking = api.resendTracking.useMutation({
    onSuccess: invalidate,
    onError: (e) => toast.error(errMsg(e)),
  });

  const conversations = React.useMemo(
    () => mergePages(state.pages, list.data?.items),
    [state.pages, list.data],
  );

  const renderMailboxActions = (m: DeskMailbox) => (
    <Select
      value={m.handledBy === 'platform' ? '__platform' : (m.handlerPartner ?? '__platform')}
      onValueChange={(v) =>
        setHandler.mutate(
          v === '__platform'
            ? { mailboxId: m.id, handledBy: 'platform', handlerPartner: null }
            : { mailboxId: m.id, handledBy: 'partner', handlerPartner: v },
        )
      }
    >
      <SelectTrigger className="h-8 w-[170px] text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__platform">TGD Care desk</SelectItem>
        {Array.from(
          new Set([...partnerDeskOptions, ...(m.handlerPartner ? [m.handlerPartner] : [])]),
        ).map((slug) => (
          <SelectItem key={slug} value={slug}>
            Partner: {slug}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <RxSupportDesk
      title="Rx control center"
      subtitle="Patient support for every affiliate brand, answered by the TGD Care desk."
      currentUserId={state.currentUserId}
      counts={counts.data}
      mailboxes={mailboxes.data}
      mailboxesLoading={mailboxes.isLoading}
      conversations={conversations}
      conversationsLoading={list.isLoading}
      hasMore={Boolean(list.data?.nextCursor)}
      onLoadMore={() => {
        if (!list.data?.nextCursor) return;
        state.setPages((prev) => [...prev, list.data?.items ?? []]);
        state.setCursor(list.data.nextCursor);
      }}
      filters={state.filters}
      onFiltersChange={state.onFiltersChange}
      selectedId={state.selectedId}
      onSelect={state.setSelectedId}
      detail={detail.data}
      detailLoading={detail.isLoading}
      onReply={async (i) => {
        await reply.mutateAsync(i);
        toast.success('Reply sent');
      }}
      onNote={async (i) => {
        await note.mutateAsync(i);
      }}
      onSetStatus={async (i) => {
        await setStatus.mutateAsync(i);
      }}
      onAssign={async (i) => {
        await assign.mutateAsync(i);
      }}
      onRefreshMailbox={async (mailboxId) => {
        await refreshMailbox.mutateAsync({ mailboxId });
      }}
      context={context.data}
      contextLoading={context.isLoading}
      caseActions={
        state.selectedId
          ? {
              onRefund: async (i) => {
                const r = await refundCase.mutateAsync({ ...i, conversationId: state.selectedId! });
                toast.success(r.voided && r.refundedAmount === 0 ? 'Payment hold released' : 'Refund sent');
              },
              onCancelRenewal: async (i) => {
                await cancelRenewal.mutateAsync({ ...i, conversationId: state.selectedId! });
                toast.success('Renewals stopped');
              },
              onResendTracking: async (i) => {
                await resendTracking.mutateAsync({ ...i, conversationId: state.selectedId! });
                toast.success('Tracking email sent');
              },
            }
          : undefined
      }
      renderMailboxActions={canMoveDesk ? renderMailboxActions : undefined}
    />
  );
}
